import React, { useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import styles from './McpServerDetail.module.scss';
import { INVALID_SCHEMA_COPY as COPY } from './mcpUtils';

/**
 * Renders the result of `GET /{id}/lint` as two lists.
 *
 * Blocking findings refuse a publish server-side, so they are shown as they are
 * discovered — the customer should never learn about them from a 400 on the
 * publish button. Warnings never block.
 *
 * Shared by the detail header (as an expandable summary) and the publish dialog.
 *
 * `invalidTools` comes from the structured `invalid_tools` list, never from
 * the blocking text, and is listed under the finding that mentions it. Each
 * entry offers the two fixes that act on one tool (edit the schema, disable
 * it); re-importing in sync mode is offered once for the lot.
 */
const INVALID_SCHEMA_FINDING = /invalid input schema/i;

const LintPanel = ({
  lint,
  loading = false,
  dense = false,
  published = false,
  onOpenTool,
  onDisableTool,
  onReimportSync,
}) => {
  const blocking = lint?.blocking || [];
  const warnings = lint?.warnings || [];
  const invalidTools = lint?.invalidTools || [];
  const [disabling, setDisabling] = useState(null);

  const handleDisable = async (toolId) => {
    setDisabling(toolId);
    try {
      await onDisableTool(toolId);
    } finally {
      setDisabling(null);
    }
  };

  // Attach the list to the finding it explains; if the wording ever changes,
  // it still shows — after the last blocking finding.
  const anchorIndex = (() => {
    const i = blocking.findIndex((f) => INVALID_SCHEMA_FINDING.test(f.raw));
    return i === -1 ? blocking.length - 1 : i;
  })();

  const renderInvalidTools = () => (
    <div className={styles.lintTools}>
      <ul className={styles.lintToolList}>
        {invalidTools.map((t) => (
          <li key={t.toolId} className={styles.lintToolItem}>
            <div className={styles.lintToolHead}>
              {onOpenTool ? (
                <Tooltip title={COPY.openTooltip} arrow>
                  <button
                    type="button"
                    className={styles.lintToolLink}
                    onClick={() => onOpenTool(t.toolId)}
                  >
                    {t.toolName}
                  </button>
                </Tooltip>
              ) : (
                <span className={styles.lintToolName}>{t.toolName}</span>
              )}
              {onDisableTool && (
                <Tooltip title={COPY.disableTooltip} arrow>
                  <span>
                    <button
                      type="button"
                      className={styles.lintActionBtn}
                      onClick={() => handleDisable(t.toolId)}
                      disabled={disabling != null}
                    >
                      {disabling === t.toolId ? 'Disabling…' : 'Disable'}
                    </button>
                  </span>
                </Tooltip>
              )}
            </div>
            {t.reason && <span className={styles.lintDetail}>{t.reason}</span>}
          </li>
        ))}
      </ul>
      <span className={styles.lintToolsHint}>
        {COPY.listHint}
        {onReimportSync && (
          <>
            {' '}
            Already fixed the spec?{' '}
            <Tooltip title={COPY.reimportTooltip} arrow>
              <button type="button" className={styles.lintToolLink} onClick={onReimportSync}>
                Re-import it (sync)
              </button>
            </Tooltip>
          </>
        )}
      </span>
    </div>
  );

  if (loading && !blocking.length && !warnings.length) {
    return (
      <Box className={styles.lintLoading}>
        <CircularProgress size={14} />
        <span>Checking…</span>
      </Box>
    );
  }

  if (!blocking.length && !warnings.length) {
    return (
      <Box className={`${styles.lintOk} ${dense ? styles.lintDense : ''}`}>
        <CheckCircle2 size={16} />
        <span>
          {published
            ? 'Everything checks out — no issues with this server.'
            : 'Everything checks out — ready to publish.'}
        </span>
      </Box>
    );
  }

  return (
    <Box className={`${styles.lintPanel} ${dense ? styles.lintDense : ''}`}>
      {blocking.length > 0 && (
        <div className={styles.lintGroup}>
          <Typography className={`${styles.lintHeading} ${styles.lintHeadingBlocking}`}>
            <ShieldAlert size={15} />
            {blocking.length} {blocking.length === 1 ? 'issue blocks' : 'issues block'} publishing
          </Typography>
          <ul className={styles.lintList}>
            {blocking.map((f, index) => (
              <li key={f.id} className={styles.lintItemBlocking}>
                {f.message}
                {f.detail && <span className={styles.lintDetail}>{f.detail}</span>}
                {index === anchorIndex && invalidTools.length > 0 && renderInvalidTools()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className={styles.lintGroup}>
          <Typography className={`${styles.lintHeading} ${styles.lintHeadingWarning}`}>
            <AlertTriangle size={15} />
            {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}
          </Typography>
          <ul className={styles.lintList}>
            {warnings.map((f) => (
              <li key={f.id} className={styles.lintItemWarning}>
                {f.message}
                {f.detail && <span className={styles.lintDetail}>{f.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Box>
  );
};

export default LintPanel;
